import httpClient, { handleAPIError, createSuccessResponse } from './httpClient.js';
import { apiRetryManager } from '../retryManager.js';

/**
 * MineAPI - Handles all mine-related API operations
 *
 * Provides endpoints for fetching mine data, searching mines,
 * and retrieving mine details
 */
export class MineAPI {
  constructor() {
    this.client = httpClient;
  }

  /**
   * Fetch mines within a specific viewport with filtering
   *
   * @param {Object} params - Parameters for viewport request
   * @param {Object} params.bounds - Viewport bounds {west, south, east, north}
   * @param {number} params.zoom - Map zoom level
   * @param {Object} params.filters - Applied filters
   * @returns {Promise<Object>} Response with mine data in GeoJSON format
   */
  async fetchMinesInViewport(params) {
    const context = {
      operation: 'fetchMinesInViewport',
      bounds: params.bounds,
      zoom: params.zoom
    };

    try {
      return await apiRetryManager.retryViewportLoad(async () => {
        // Validate and normalize bounds - use world bounds as fallback
        let bounds = params.bounds;
        if (!bounds || bounds.west === undefined || bounds.south === undefined ||
            bounds.east === undefined || bounds.north === undefined) {
          console.warn('⚠️ Invalid bounds provided, using world bounds as fallback:', bounds);
          bounds = {
            west: -180,
            south: -90,
            east: 180,
            north: 90
          };
        } else {
          // Additional coordinate validation and normalization
          const normalizeLng = (lng) => {
            while (lng > 180) lng -= 360;
            while (lng < -180) lng += 360;
            return lng;
          };

          const clampLat = (lat) => Math.max(-90, Math.min(90, lat));

          bounds = {
            west: normalizeLng(bounds.west),
            south: clampLat(bounds.south),
            east: normalizeLng(bounds.east),
            north: clampLat(bounds.north)
          };
        }

        const response = await this.client.get('/mines/viewport', {
          params: {
            // Spatial parameters - bbox format expected by API
            bbox: `${bounds.west},${bounds.south},${bounds.east},${bounds.north}`,
            zoom: params.zoom || 2,
            // Filter parameters (automatically injected by request interceptor)
            ...params.filters
          }
        });

        return createSuccessResponse(response.data, response.status);
      }, context);
    } catch (error) {
      return handleAPIError(error, context);
    }
  }

  /**
   * Search mines by query string
   *
   * @param {string} query - Search query
   * @returns {Promise<Object>} Response with search results
   */
  async searchMines(query) {
    const context = {
      operation: 'searchMines',
      query: query
    };

    try {
      return await apiRetryManager.retrySearch(async () => {
        const response = await this.client.get('/mines/search', {
          params: { q: query }
        });
        return createSuccessResponse(response.data, response.status);
      }, context);
    } catch (error) {
      return handleAPIError(error, context);
    }
  }

  /**
   * Get detailed information for a specific mine
   *
   * @param {string|number} mineId - Unique mine identifier
   * @returns {Promise<Object>} Response with mine details
   */
  async getMineDetails(mineId) {
    const context = {
      operation: 'getMineDetails',
      mineId: mineId
    };

    try {
      const response = await this.client.get(`/mines/${mineId}`);
      return createSuccessResponse(response.data, response.status);
    } catch (error) {
      return handleAPIError(error, context);
    }
  }

  /**
   * Get search suggestions for the search bar
   * Transforms mine search results into suggestion format
   *
   * @param {string} query - Search query
   * @param {Object} options - Search options
   * @param {number} options.limit - Maximum number of suggestions (default: 10)
   * @returns {Promise<Object>} Response with formatted suggestions
   */
  async searchSuggestions(query, options = {}) {
    try {
      const params = {
        q: query,
        limit: options.limit || 10
      };

      const response = await this.client.get('/mines/search', { params });

      // Transform mine results into search suggestion format
      const suggestions = (response.data.results || []).map(mine => ({
        id: mine.mine_id,
        title: mine.mine_name || 'Unknown Mine',
        type: 'mine',
        subtitle: `${mine.commodity || 'Unknown'} • ${mine.country || 'Unknown'}`,
        // Try coordinates array first, then fall back to individual lat/lng
        coordinates: mine.coordinates ||
                    (mine.latitude && mine.longitude ? [mine.longitude, mine.latitude] : null),
        data: mine
      }));

      return {
        success: true,
        suggestions: suggestions,
        status: response.status
      };
    } catch (error) {
      return handleAPIError(error, { operation: 'searchSuggestions' });
    }
  }
}

// Export singleton instance
export const mineAPI = new MineAPI();